import { Controller, type Control } from "react-hook-form";
import type { ProjectMetadataField } from "@/types/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const EM_DASH = "—";

function getOptions(field: ProjectMetadataField): string[] {
  const raw = field.options;
  if (Array.isArray(raw)) return raw.map((v) => String(v));
  if (raw && typeof raw === "object" && "choices" in raw) {
    const choices = (raw as { choices?: unknown }).choices;
    if (Array.isArray(choices)) return choices.map((v) => String(v));
  }
  return [];
}

function FieldWrapper({
  label,
  required,
  children,
  error,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function renderField(
  field: ProjectMetadataField,
  control: Control<Record<string, unknown>>,
  disabled = false,
): React.ReactNode {
  const isRemarks = field.name.toLowerCase() === "remarks";

  return (
    <Controller
      key={field.id}
      name={field.id}
      control={control}
      render={({ field: rhf, fieldState }) => {
        const error = fieldState.error?.message;

        if (disabled) {
          return (
            <FieldWrapper label={field.name}>
              <ViewField field={field} value={rhf.value} />
            </FieldWrapper>
          );
        }

        switch (field.field_type) {
          case "text":
            return (
              <FieldWrapper
                label={field.name}
                required={field.required}
                error={error}
              >
                {isRemarks ? (
                  <Textarea
                    value={(rhf.value as string) ?? ""}
                    onChange={rhf.onChange}
                    onBlur={rhf.onBlur}
                    rows={3}
                  />
                ) : (
                  <Input
                    type="text"
                    value={(rhf.value as string) ?? ""}
                    onChange={rhf.onChange}
                    onBlur={rhf.onBlur}
                  />
                )}
              </FieldWrapper>
            );
          case "number":
            return (
              <FieldWrapper
                label={field.name}
                required={field.required}
                error={error}
              >
                <Input
                  type="number"
                  value={(rhf.value as string | number) ?? ""}
                  onChange={rhf.onChange}
                  onBlur={rhf.onBlur}
                />
              </FieldWrapper>
            );
          case "date":
            return (
              <FieldWrapper
                label={field.name}
                required={field.required}
                error={error}
              >
                <Input
                  type="date"
                  value={(rhf.value as string) ?? ""}
                  onChange={rhf.onChange}
                  onBlur={rhf.onBlur}
                />
              </FieldWrapper>
            );
          case "boolean":
            return (
              <FieldWrapper label={field.name} required={field.required} error={error}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(rhf.value)}
                    onChange={(e) => rhf.onChange(e.target.checked)}
                    onBlur={rhf.onBlur}
                    className="h-4 w-4 rounded border-border"
                  />
                  <span className="text-muted-foreground">
                    {rhf.value ? "Yes" : "No"}
                  </span>
                </label>
              </FieldWrapper>
            );
          case "single_select": {
            const options = getOptions(field);
            return (
              <FieldWrapper
                label={field.name}
                required={field.required}
                error={error}
              >
                <select
                  value={(rhf.value as string) ?? ""}
                  onChange={(e) => rhf.onChange(e.target.value)}
                  onBlur={rhf.onBlur}
                  className="h-9 w-full rounded-md border border-border bg-transparent px-3 text-sm"
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </FieldWrapper>
            );
          }
          case "multi_select": {
            const options = getOptions(field);
            const selected = Array.isArray(rhf.value)
              ? (rhf.value as string[])
              : [];
            const toggle = (opt: string) => {
              const next = selected.includes(opt)
                ? selected.filter((v) => v !== opt)
                : [...selected, opt];
              rhf.onChange(next);
            };
            return (
              <FieldWrapper
                label={field.name}
                required={field.required}
                error={error}
              >
                <div className="flex flex-wrap gap-1.5">
                  {options.length === 0 && (
                    <p className="text-xs text-muted-foreground">No options</p>
                  )}
                  {options.map((opt) => {
                    const isSelected = selected.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggle(opt)}
                        className={cn(
                          "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-transparent hover:bg-muted",
                        )}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </FieldWrapper>
            );
          }
          default:
            return <div />;
        }
      }}
    />
  );
}

function ViewField({
  field,
  value,
}: {
  field: ProjectMetadataField;
  value: unknown;
}) {
  if (field.field_type === "boolean") {
    return <p className="text-sm">{value ? "Yes" : "No"}</p>;
  }
  if (field.field_type === "multi_select") {
    const values = Array.isArray(value) ? (value as string[]) : [];
    if (values.length === 0) return <p className="text-sm">{EM_DASH}</p>;
    return (
      <div className="flex flex-wrap gap-1">
        {values.map((v) => (
          <Badge key={v} variant="secondary">
            {v}
          </Badge>
        ))}
      </div>
    );
  }
  const text =
    value === undefined || value === null || value === "" ? EM_DASH : String(value);
  return <p className="whitespace-pre-wrap text-sm">{text}</p>;
}
